import { useState, useRef, useEffect } from 'react';
import { FaFileAlt, FaCloudUploadAlt, FaFilePdf, FaFileWord, FaMagic } from 'react-icons/fa';
import api from '../../api';

function downloadBase64(base64, filename, mime) {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function ResumeBooster() {
  const [file, setFile]           = useState(null);
  const [jobDescription, setJobDescription] = useState('');
  const [extraInstructions, setExtraInstructions] = useState('');
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [result, setResult]       = useState(null); // { resume, pdfBase64, docxBase64, pdfFilename, docxFilename }
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.getResumeTemplates()
      .then(({ templates, defaultTemplate }) => {
        setTemplates(templates);
        setTemplate(defaultTemplate);
      })
      .catch(() => {});
  }, []);

  const handleFilePick = (f) => {
    if (!f) return;
    const ok = /^(application\/pdf|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document)$/i.test(f.type);
    if (!ok) { setError('Only PDF or Word (.docx) files are accepted.'); return; }
    setError('');
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please upload a resume file.'); return; }

    setLoading(true); setError(''); setResult(null);
    try {
      const data = await api.tailorResume(file, jobDescription, extraInstructions, template);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null); setJobDescription(''); setExtraInstructions(''); setResult(null); setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#0c1b4d] flex items-center justify-center">
            <FaMagic className="text-[#D4AF37]" size={16} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#0c1b4d]">Resume Booster</h1>
            <p className="text-xs text-gray-500">Boost a resume's recent experience to match a job description using AI</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Resume file (PDF or Word)</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFilePick(e.dataTransfer.files?.[0]); }}
              className="flex items-center gap-3 border-2 border-dashed border-gray-300 rounded-lg px-4 py-6 cursor-pointer hover:border-[#0c1b4d] hover:bg-blue-50/40 transition-colors"
            >
              <FaCloudUploadAlt className="text-gray-400 shrink-0" size={28} />
              <div className="min-w-0">
                {file ? (
                  <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-700">Click to upload or drag &amp; drop</p>
                    <p className="text-xs text-gray-500">PDF or .docx, up to 10MB</p>
                  </>
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => handleFilePick(e.target.files?.[0])}
            />
          </div>

          {templates.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Resume template</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {templates.map((t) => (
                  <button
                    type="button"
                    key={t.key}
                    onClick={() => setTemplate(t.key)}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium border text-left transition-colors ${
                      template === t.key
                        ? 'border-[#0c1b4d] bg-blue-50 text-[#0c1b4d]'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Job description <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              rows={8}
              placeholder="Paste the target job posting here, or leave blank to just spell-check and format the resume without tailoring it to a role..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]/30 focus:border-[#0c1b4d]"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Additional instructions <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={extraInstructions}
              onChange={(e) => setExtraInstructions(e.target.value)}
              rows={3}
              placeholder={`e.g. "Emphasize the cloud migration project", "Add a one-line project summary before each role's bullets", "Call these Projects, not Employers"`}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1b4d]/30 focus:border-[#0c1b4d]"
            />
            <p className="text-xs text-gray-400 mt-1">Extra guidance for the AI beyond the job description — which project to emphasize, summary style, terminology, etc.</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#0c1b4d] text-white text-sm font-semibold rounded-lg hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FaMagic size={13} />
              {loading ? 'Boosting resume…' : 'Boost Resume'}
            </button>
            {(file || jobDescription || extraInstructions || result) && (
              <button type="button" onClick={reset} className="text-sm text-gray-500 hover:text-gray-700">
                Reset
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400">
If a job description is provided, the resume's 2 most recent roles/projects are rewritten to align with it. Without one, the resume is only spell/grammar-checked and formatted, not rewritten. Nothing is stored — files are processed in memory only.
          </p>
        </form>

        {/* Result */}
        {result && (
          <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[#0c1b4d] flex items-center gap-2">
                <FaFileAlt className="text-[#D4AF37]" /> Boosted Resume Preview
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={() => downloadBase64(result.pdfBase64, result.pdfFilename, 'application/pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FaFilePdf className="text-red-500" size={14} /> PDF
                </button>
                <button
                  onClick={() => downloadBase64(result.docxBase64, result.docxFilename, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FaFileWord className="text-blue-600" size={14} /> Word
                </button>
              </div>
            </div>

            <ResumePreview resume={result.resume} />
          </div>
        )}
      </div>
    </div>
  );
}

function ResumePreview({ resume: r }) {
  if (!r) return null;
  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-gray-50/50 text-sm">
      <h3 className="text-lg font-bold text-[#0c1b4d] text-center">{r.contact?.name}</h3>
      {r.contact?.title && <p className="text-sm text-[#B8860B] italic text-center mb-1">{r.contact.title}</p>}
      <p className="text-xs text-gray-500 mb-3 text-center">
        {[r.contact?.email, r.contact?.phone, r.contact?.location, r.contact?.linkedin].filter(Boolean).join('  •  ')}
      </p>

      {r.summary && (
        <div className="mb-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Summary</p>
          <p className="text-gray-700">{r.summary}</p>
        </div>
      )}

      {Array.isArray(r.skills) && r.skills.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Skills</p>
          <div className="flex flex-wrap gap-1.5">
            {r.skills.map((s, i) => (
              <span key={i} className="px-2 py-0.5 bg-blue-50 text-[#0c1b4d] rounded text-xs">{s}</span>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(r.experience) && r.experience.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Experience</p>
          <div className="space-y-3">
            {r.experience.map((job, i) => (
              <div key={i} className={i < 2 ? 'border-l-2 border-[#D4AF37] pl-3' : 'pl-3'}>
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-semibold text-gray-800">{job.title}{job.company ? ` — ${job.company}` : ''}</p>
                  <p className="text-xs text-gray-500 shrink-0">{job.dates}</p>
                </div>
                {job.location && <p className="text-xs text-gray-500 italic">{job.location}</p>}
                {job.projectSummary && <p className="text-xs text-gray-600 italic mt-1">{job.projectSummary}</p>}
                <ul className="list-disc list-inside text-gray-700 mt-1 space-y-0.5">
                  {(job.bullets || []).map((b, bi) => <li key={bi}>{b}</li>)}
                </ul>
                {Array.isArray(job.techStack) && job.techStack.length > 0 && (
                  <p className="text-xs text-gray-600 mt-1">
                    <span className="font-semibold text-[#B8860B]">Tech Stack:</span> {job.techStack.join(', ')}
                  </p>
                )}
                {i < 2 && <p className="text-[10px] text-[#B8860B] font-semibold mt-1">✦ Boosted for job description</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {Array.isArray(r.education) && r.education.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Education</p>
          {r.education.map((ed, i) => (
            <p key={i} className="text-gray-700">{[ed.degree, ed.school, ed.location, ed.year].filter(Boolean).join(' — ')}</p>
          ))}
        </div>
      )}

      {Array.isArray(r.certifications) && r.certifications.length > 0 && (
        <div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Certifications</p>
          <p className="text-gray-700">{r.certifications.join('  •  ')}</p>
        </div>
      )}
    </div>
  );
}
